import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { UpgradePlanModal } from 'components/Modals';
import { useCallback, useRef, useState } from 'react';
import useRegionStore from 'store/useRegionStore';
import useUserStore from 'store/useUserStore';
import { DEFAULT_REGION, Region } from 'types/Region';
import { containsWithoutSpecialChars } from 'util/containsWithoutSpecialChars';
import DefaultDropDown from './DefaultDropDown';

interface CityFilterProps {
    lock?: boolean;
    placeholder?: string;
    region: Region;
    selectedCities?: string[];
    onChanged?: (cities: string[]) => void;
    selectedStates?: string[];
}

const CityFilter = ({
    lock = false,
    selectedCities = [],
    onChanged = () => {},
    region = DEFAULT_REGION,
    placeholder = 'Area',
    selectedStates = [],
}: CityFilterProps) => {
    const [showUpgradePlanModal, setShowUpgradePlanModal] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const childRef = useRef<{ closePopup: () => void }>(null);
    const { getFilteredCities, getCities, getNonCities } = useRegionStore();
    const { isPremiumUser } = useUserStore();

    // Toggle checkbox selection for a city.
    const toggleCitySelection = useCallback(
        (city: string) => {
            let newSelection: string[];
            if (selectedCities.includes(city)) {
                newSelection = selectedCities.filter((c) => c !== city);
            } else {
                newSelection = [...selectedCities, city];
            }
            onChanged(newSelection);
        },
        [selectedCities, onChanged],
    );

    const onSearchTermChanged = (text: string) => setSearchTerm(text);
    const onClearClicked = () => onChanged([]);
    const onShowModal = () => {
        if (childRef.current) {
            childRef.current.closePopup();
        }
        if (!isPremiumUser()) setShowUpgradePlanModal(true);
    };

    // Combine cities from all selected states, or fallback if none are selected.
    let cities: string[] = [];
    let nonCities: string[] = [];
    if (selectedStates && selectedStates.length > 0) {
        cities = selectedStates.flatMap((state) => getCities(state, region)).filter((city): city is string => city !== undefined);
        nonCities = selectedStates.flatMap((state) => getNonCities(state, region)).filter((city): city is string => city !== undefined);
    } else {
        cities = getCities('', region).filter((city): city is string => city !== undefined);
        nonCities = getNonCities('', region).filter((city): city is string => city !== undefined);
    }

    // Sort both lists alphabetically
    cities.sort((a, b) => a.localeCompare(b));
    nonCities.sort((a, b) => a.localeCompare(b));

    // Define dropdown title with multi-select support.
    const titleContent = selectedCities.length === 0 ? placeholder : <div className="truncate">{selectedCities.join(', ')}</div>;

    return (
        <>
            <DefaultDropDown
                ref={childRef}
                Title={<div className="truncate">{titleContent}</div>}
                IsEmpty={selectedCities.length === 0}
                onClearClicked={onClearClicked}
            >
                {/* Search Input */}
                <div className="px-[10px] py-[5px]">
                    <div className="rounded-[5px] border border-my-gray border-opacity-60 flex items-center justify-between gap-[5px] px-[10px] py-[5px]">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => onSearchTermChanged(e.target.value)}
                            className="flex-grow focus:outline-none w-full"
                        />
                        <SearchOutlinedIcon />
                    </div>
                </div>
                <div className="max-h-[300px] overflow-y-scroll">
                    {/* When no region and no selectedStates, show filtered cities */}
                    {!region.name &&
                        (!selectedStates || selectedStates.length === 0) &&
                        getFilteredCities(searchTerm).map((city, index) =>
                            lock && city.lock ? (
                                <label key={index} className="flex items-center gap-2 p-2 cursor-pointer opacity-50" onClick={onShowModal}>
                                    <input type="checkbox" checked={selectedCities.includes(city.title)} disabled />
                                    <span>{city.title}</span>
                                </label>
                            ) : (
                                <label key={index} className="flex items-center gap-2 p-2 cursor-pointer">
                                    <input type="checkbox" checked={selectedCities.includes(city.title)} onChange={() => toggleCitySelection(city.title)} />
                                    <span>{city.title}</span>
                                </label>
                            ),
                        )}

                    {/* Render cities from combined lists */}
                    {(lock ? cities : [...cities, ...nonCities])
                        .filter((city) => containsWithoutSpecialChars(city, searchTerm))
                        .map((city, index) => (
                            <label key={index} className="flex items-center gap-2 p-2 cursor-pointer">
                                <input type="checkbox" checked={selectedCities.includes(city)} onChange={() => toggleCitySelection(city)} />
                                <span>{city}</span>
                            </label>
                        ))}
                    {lock &&
                        nonCities
                            .filter((city) => containsWithoutSpecialChars(city, searchTerm))
                            .map((city, index) => (
                                <label key={index} className="flex items-center gap-2 p-2 cursor-pointer opacity-50" onClick={onShowModal}>
                                    <input type="checkbox" checked={selectedCities.includes(city)} disabled />
                                    <span>{city}</span>
                                </label>
                            ))}
                </div>
            </DefaultDropDown>

            <UpgradePlanModal visible={showUpgradePlanModal} onClose={() => setShowUpgradePlanModal(false)} />
        </>
    );
};

export default CityFilter;
